import { StatusFile } from '@/utils/enum';

export type BeraResultInformations = {
	id: string;
	organization_name: string;
	company_name: string;
	sector_name: string;
	line_name: string;
	workstation_name: string;
	workstation_id: string;
	cycle_name: string;
	evaluator: string;
	work_center?: string;
	operator_evaluated?: string;
	file_id: string;
	collection_date: Date;
	overall_score: number;
	total_time: string;
	weighted_rsi?: number;
	gns?: boolean;
};

export type BeraResultAnalyzedFiles = {
	id: string;
	task_name: string;
	file_name: string;
	file_url?: string;
	total_time: number;
};

export type RPNScore = {
	step_key_id: string;
	step_key_sequence: number;
	step_sequence: number;
	task_rpn: number;
};

export type StepKeyRpn = {
	id: string;
	task_name: string;
	task_id: string;
	rpn: number;
	rpn_scores: RPNScore[];
};

export type BeraResultCycleSummary = {
	id: string;
	overall_score: number;
	task_rpn_scores: StepKeyRpn[];
	step_keys_weighted_rpn: RPNScore[];
};

export type BeraResultRPNScoresByTask = {
	id: string;
	order: number;
	task_name: string;
	rpn: number;
	file_name: string;
};

export type BeraResultRPNScoresByStepKey = BeraResultRPNScoresByTask & {
	step_key_description: string;
};

export type BeraResultHighestRPNScores = {
	by_task: BeraResultRPNScoresByTask[];
	by_step_key: BeraResultRPNScoresByStepKey[];
};

export type TaskResult = {
	id: string;
	name: string;
	file_id: string;
	file_name: string;
	bera_report_id: string;
};

export type BeraClipFile = {
	id: string;
	original_name: string;
	status: StatusFile;
	duration: number | null;
	url: string | null;
	processing_error: string | null;
};

export type BeraTaskResultV2 = TaskResult & {
	thumbnail_url: string | null;
	segments: BeraReportV2Segment[];
	file: BeraClipFile | null;
};

export type BeraClipStatusEvent = {
	bera_job_summary_id: string;
	bera_report_id: string;
	file: Pick<BeraClipFile, 'id' | 'status'>;
};

export type BeraSummaryResult = {
	id: string;
	comment?: string;
	informations: BeraResultInformations;
	analyzed_files: BeraResultAnalyzedFiles[];
	cycle_summary: BeraResultCycleSummary;
	highest_rpn_scores: BeraResultHighestRPNScores;
	tasks: TaskResult[];
	gns: boolean;
	consolidated: boolean;
};

export type BeraSummaryResultV2 = Omit<BeraSummaryResult, 'tasks'> & {
	version: number;
	file: BeraClipFile | null;
	tasks: BeraTaskResultV2[];
};

export type BeraSummaryFinishV2 = {
	bera_job_summary: BeraJobSummary;
	dispatched_bera_report_ids: string[];
};

export type TaskTimeFormat = 'minutes' | 'seconds';

export interface URLDownloadDTO {
	url: string;
}

export enum Risk {
	VERY_LOW = 'VERY_LOW',
	LOW = 'LOW',
	MODERATE = 'MODERATE',
	HIGH = 'HIGH',
	VERY_HIGH = 'VERY_HIGH'
}

export type Niosh = {
	id: string;
	mass_m: string;
	recommended_weight_limit: string;
	lifting_index: string;
	risk: Risk;
	reference_weight: number;
};

export enum ForceIntensity {
	VERY_LOW = 'VERY_LOW',
	MODERATE = 'MODERATE',
	HIGH = 'HIGH',
	VERY_HIGH = 'VERY_HIGH',
	PEAK = 'PEAK',
	POWERFUL_HITTING = 'POWERFUL_HITTING'
}
export enum ForceTranser {
	OPTIMUM = 'OPTIMUM',
	RESTRICTED = 'RESTRICTED',
	HINDERED = 'HINDERED'
}

export enum RiskLoad {
	LOW = 'LOW',
	SLIGHTLY_INCREASED = 'SLIGHTLY_INCREASED',
	SUBSTANTIALLY_INCREASED = 'SUBSTANTIALLY_INCREASED',
	HIGH = 'HIGH'
}

export type KimManualHandling = {
	id: string;
	duration: number;
	risk_score: number;
	risk_load: RiskLoad;
	force_transfer: ForceTranser;
	left_force_intensity: ForceIntensity;
	right_force_intensity: ForceIntensity;
};

enum RiskKey {
	SAFE = 'SAFE',
	HAZARDOUS = 'HAZARDOUS'
}

type OverallScoreRisk = RiskKey.SAFE | RiskKey.HAZARDOUS;

export type StrainIndex = {
	id: string;
	left_risk: OverallScoreRisk;
	right_risk: OverallScoreRisk;
	score_left_rsi: string;
	score_right_rsi: string;
	score_left_borg_scale: string;
	score_right_borg_scale: string;
	score_left_exertion_duration: string;
	score_right_exertion_duration: string;
	score_left_efforts_per_minute: string;
	score_right_efforts_per_minute: string;
	score_left_wrist_posture: string;
	score_right_wrist_posture: string;
	score_left_daily_duration: string;
	score_right_daily_duration: string;
};

export type Result = {
	task_rpn: number;
	frequency: TaskRPNFactor;
	stress_level: TaskRPNFactor;
	total_task_duration: TaskRPNFactor;
	job_element: string;
};

export type StepKeyResult = {
	id: string;
	description: string;
	name: string;
	result: Result;
};

export type StepResult = {
	id: string;
	name: string;
	description: string;
	step_key: StepKeyResult[];
};

export type RPN = {
	exposure: number;
	severity: number;
	vulnerability: number;
	rpn: number;
};

export type TaskRPNFactor = {
	id: string;
	name: string;
	description: string;
};

export type InformationsOrganization = {
	name: string;
	company_name: string;
	sector_name: string;
	line_name: string;
	workstation_name: string;
};

export type CycleRPN = {
	severity: number;
	exposure: number;
	vulnerability: number;
	rpn: number;
};

export type InformationsCycle = {
	collection_date: Date;
	cycle_name: string;
	evaluator: string;
	file_name: string;
	has_known_injury: boolean;
	operator_evaluated: string;
	rpn: RPN;
	task_name: string;
	work_center: string;
	task_time: number;
	task_time_format: TaskTimeFormat;
	comment: string;
	consolidated: boolean;
	bera_job_summary_id: string;
};

export type BeraReportResult = {
	bera_report_id: string;
	organization: InformationsOrganization;
	cycle: InformationsCycle;
	bera_steps_result: StepResult[];
	tools: {
		niosh: Niosh;
		kim_mho: KimManualHandling;
		strain_index: StrainIndex;
	};
};

export type Task = {
	id: string;
	name: string;
};

export type CustomReportStepKey = {
	id: string;
	name: string;
	description: string;
};

export type NoENSTask = {
	id: string;
	task_rpn: number;
	task: Task;
	custom_report_step_key: CustomReportStepKey;
	position: number;
};

export type ResponsibleUser = {
	id: string;
	name: string;
};

export type ActionPlanTask = {
	id: string;
	title: string;
	due_date: string;
	responsible: ResponsibleUser;
	tasks: ActionPlanTask[];
};

export type ActionPlan = {
	id: string;
	title: string;
	due_date: string;
	description: string;
	responsible: ResponsibleUser;
	tasks: ActionPlanTask[];
};

export type Workstation = {
	id: string;
	name: string;
};

export type Line = {
	id: string;
	name: string;
};

export type CustomReportStep = {
	id: string;
	name: string;
};

export type ENSWorkstation = {
	workstation: Workstation;
	line: Line;
	overall_score: number;
};

export type NOENSWorkstation = {
	workstation: Workstation;
	line: Line;
	custom_report_step: CustomReportStep;
	overall_score: number;
};

export type DashboardENS = {
	labels: string[];
	gns: {
		total: number;
		data: number[] | null;
	};
	no_gns: {
		total: number;
		data: number[] | null;
	};
	not_evaluated: {
		total: number;
		data: number[] | null;
	};
};

export type BeraReportV2Segment = {
	id: string;
	segment_start: string;
	segment_end: string;
	sequence: number;
	duration: number;
	source_file_id?: string | null;
	source_file_name?: string | null;
};

export type BeraReportV2 = {
	id: string;
	task_id: string;
	file_id: string;
	bera_job_summary_id: string;
	task_name: string;
	segments: BeraReportV2Segment[];
	has_evaluation: number;
	is_evaluated: number;
	step: number;
	rpn?: number | null;
};

export type BeraJobSummary = {
	id: string;
	workstation_id: string;
	cycle_id?: string;
	show_bera_result?: boolean;
	cycle_name?: string;
	sector_name?: string;
	line_name?: string;
	workstation_name?: string;
	evaluator_name?: string | null;
	operator_evaluated?: string | null;
	work_center?: string | null;
	is_revision?: boolean;
	file?: BeraJobSummaryFile | null;
};

export type BeraJobSummaryFile = {
	id: string;
	status?: StatusFile;
	processing_error?: string | null;
};

export type BeraVideoPatchStatusEvent = {
	bera_job_summary_id: string;
	file: BeraJobSummaryFile;
};

export type BeraCustomReportStepKey = {
	id: string;
	name: string;
	description: string;
};

export type BeraCustomReportStep = {
	id: string;
	name: string;
	description: string;
	sequence: number;
	step_key: BeraCustomReportStepKey[];
};

export type BeraReportV2StepKeyResult = {
	id: string;
	step_id: string;
	step_key_id: string;
	stress_level_id: string | null;
	frequency_id: string | null;
	total_task_duration_id: string | null;
	job_element: string | null;
	task_rpn: number;
};

export type BeraStepKeyReference = Pick<BeraReportV2StepKeyResult, 'step_id' | 'step_key_id'>;

export type BeraReportV2Details = {
	id: string;
	task_id: string;
	file_id: string;
	bera_job_summary_id: string;
	task_time: number | null;
	task_time_format: 'minutes' | 'seconds' | null;
	has_known_injury: boolean | null;
	collection_date: string | null;
	has_no_risk: boolean;
	comment: string | null;
	step: number;
	reached_step: number;
	consolidated: boolean;
	segments: BeraReportV2Segment[];
	step_key_results: BeraReportV2StepKeyResult[];
};

export type BeraReportV2ResultFactor = {
	id: string;
	name: string;
	description: string;
	score: number;
};

export type BeraReportV2SelectedFactor = BeraReportV2ResultFactor | null;

export type BeraReportV2StepKeyOptions = {
	stepKeyId: string;
	stressLevels: BeraReportV2ResultFactor[];
	frequencies: BeraReportV2ResultFactor[];
	totalTaskDurations: BeraReportV2ResultFactor[];
};

export type BeraReportV2StepKeyRow = {
	id: string;
	name: string;
	description: string;
	hasPresence: boolean;
	stressLevel: BeraReportV2SelectedFactor;
	frequency: BeraReportV2SelectedFactor;
	totalTaskDuration: BeraReportV2SelectedFactor;
	jobElement: string;
	taskRpn: number;
};

export type BeraReportV2ResultStep = {
	id: string;
	name: string;
	description: string;
	stepKeys: BeraReportV2StepKeyRow[];
};

export type BeraReportV2Scores = {
	severity: number;
	exposure: number;
	vulnerability: number;
	rpn: number;
};

export type BeraReportV2ResultInformations = {
	companyName: string;
	unitName: string;
	sectorName: string;
	lineName: string;
	workstationName: string;
	cycleName: string;
	taskTime: number | null;
	taskTimeFormat: 'minutes' | 'seconds';
	fileName: string;
	evaluatorName: string;
	workCenter: string;
	taskName: string;
	operatorEvaluated: string;
	collectionDate: string | null;
	hasKnownInjury: boolean | null;
};

export type BeraReportV2Result = {
	id: string;
	informations: BeraReportV2ResultInformations;
	scores: BeraReportV2Scores;
	steps: BeraReportV2ResultStep[];
	comment: string;
	consolidated: boolean;
};

export type BeraJobSummaryListFilter = {
	company_id_filter?: string;
	sector_id?: string;
	line_id?: string;
	workstation_id?: string;
	cycle_id?: string;
	task_id?: string;
	report_name?: string;
	created_at_start?: string;
	created_at_end?: string;
	updated_at_start?: string;
	updated_at_end?: string;
	type_overall_score?: string;
	min_overall_score?: string;
	max_overall_score?: string;
	type_rpn?: string;
	min_rpn?: string;
	max_rpn?: string;
};

export type BeraJobSummaryListQuery = BeraJobSummaryListFilter & {
	limit: number;
	offset: number;
};

export type BeraJobSummaryListItem = {
	id: string;
	version: number;
	report_name: string;
	cycle_name: string | null;
	workstation_name: string | null;
	tlid_count: number;
	overall_score: number | null;
	updated_at: string;
	versions_count: number;
	consolidated?: boolean;
};

export type BeraJobSummaryListPage = {
	count: number;
	rows: BeraJobSummaryListItem[];
};

export type BeraVersion = {
	id: string;
	name?: string;
	tlid_count: number;
	created_at: string;
	consolidated: boolean;
	is_current: boolean;
};

export const BERA_REVISION_TYPE = {
	FULL: 'FULL',
	PARTIAL: 'PARTIAL'
} as const;

export type BeraRevisionType = keyof typeof BERA_REVISION_TYPE;

export type BeraPdfDownload = {
	url: string;
};

export type BeraRevisionSource = {
	id: string;
	report_name: string;
	tlid_count: number;
	created_at: string;
};

export type BeraRevisionSourceLookup = {
	organization_id: string;
	company_id: string;
	cycle_id: string;
	workstation_id: string;
};

export type BeraRevisionOverrides = {
	report_name?: string;
	evaluator_id?: string;
	operator_evaluated?: string;
	work_center?: string;
};

export type BeraSegmentAssignment = {
	segment_id: string;
	file_id: string;
};
